import {
	type RemarkProseMirrorOptions,
	fromPmMark,
	fromPmNode,
	fromProseMirror,
	remarkProseMirror,
	toPmMark,
	toPmNode,
} from "@handlewithcare/remark-prosemirror";
import { fromHtml } from "hast-util-from-html";
import { toHtml } from "hast-util-to-html";
import {
	type Code as MdastCode,
	type Html as MdastHtml,
	type Image as MdastImage,
	type List as MdastList,
	type ListItem as MdastListItem,
	type Table as MdastTable,
	type TableRow as MdastTableRow,
	type Parent,
	type PhrasingContent,
	type Root,
	type RootContent,
	type Yaml,
} from "mdast";
import { gfmTaskListItemFromMarkdown } from "mdast-util-gfm-task-list-item";
import { gfmTaskListItem } from "micromark-extension-gfm-task-list-item";
import { normalizeUri } from "micromark-util-sanitize-uri";
import { type Node as PmNode } from "prosemirror-model";
import remarkFrontmatter from "remark-frontmatter";
import remarkParse from "remark-parse";
import remarkStringify from "remark-stringify";
import { type Processor, unified } from "unified";
import { CONTINUE, SKIP, visit } from "unist-util-visit";
import { stringify } from "yaml";

import { buildAcceptedProjectionWithMapping, schema } from "@moment/prosemirror-schema";
import { DEFAULT_CELL_ID } from "@moment/prosemirror-utils";

import { remarkArbitraryListDepth } from "./arbitraryListDepth";
import { remarkYamlMatter } from "./frontmatter";
import { remarkGfmTable } from "./gfmTable";
import { escapePageTitleForMarkdown, remarkPageLink } from "./pageLinkSyntax";
import { remarkStrikethrough } from "./strikethrough";
import { transformStripTrailingSpaceMarks } from "./transformStripTrailingSpaceMarks";

export function remarkTaskList(this: Processor) {
	const data = this.data();

	const micromarkExtensions = data.micromarkExtensions || (data.micromarkExtensions = []);
	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);

	micromarkExtensions.push(gfmTaskListItem());
	fromMarkdownExtensions.push(gfmTaskListItemFromMarkdown());
}

/**
 * A remark plugin that unwraps HTML nodes from their parent
 * paragraph nodes.
 */
export function remarkUnwrapHtml(this: Processor) {
	const data = this.data();

	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);

	fromMarkdownExtensions.push({
		transforms: [
			function (tree) {
				visit(tree, "paragraph", function (paragraph, index, parent) {
					if (!parent || index === undefined) {
						return CONTINUE;
					}

					if (paragraph.children.length !== 1) {
						return CONTINUE;
					}

					const firstChild = paragraph.children[0];
					if (firstChild?.type !== "html") {
						return CONTINUE;
					}

					if (firstChild.value === "<br>") {
						paragraph.children = [];
						return CONTINUE;
					}

					// Don't unwrap inline elements like <span> (used for page links)
					if (firstChild.value.startsWith("<span")) {
						return CONTINUE;
					}

					parent.children.splice(index, 1, firstChild);

					return SKIP;
				});
			},
			function (tree) {
				visit(tree, "text", function (paragraph) {
					// IMPORTANT: Empty text nodes are not allowed and WILL crash the parser. To be
					// extra safe, we replace it with a Unicode non-breaking space character.
					if (paragraph.value.trim() === "") {
						paragraph.value = "\u00A0";
						return SKIP;
					}
				});
			},
		],
	});
}

/**
 * Transform GitHub code snippet HTML into code block MDAST nodes
 * Must run AFTER remarkJoinHtml so HTML is already joined
 */
export function remarkTransformCodeSnippets(this: Processor) {
	const data = this.data();

	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);

	fromMarkdownExtensions.push({
		transforms: [
			function (tree) {
				visit(tree, "paragraph", function (paragraph, index, parent) {
					if (!parent || index === undefined) {
						return CONTINUE;
					}

					if (paragraph.children.length !== 1) {
						return CONTINUE;
					}

					const firstChild = paragraph.children[0];
					if (firstChild?.type !== "html") {
						return CONTINUE;
					}

					const htmlValue = firstChild.value;

					// Check if this is a GitHub code snippet with data-expanded="true"
					if (
						htmlValue.includes('data-expanded="true"') &&
						htmlValue.includes("github.com/") &&
						htmlValue.includes("/blob/")
					) {
						const hrefMatch = htmlValue.match(/href="([^"]+)"/);
						if (hrefMatch) {
							const href = hrefMatch[1];
							const m = href.match(
								/^https?:\/\/(?:www\.)?github\.com\/([^/\s]+)\/([^/\s]+)\/blob\/([^#]+)(?:#(.*))?$/
							);
							if (m) {
								const owner = m[1] as string;
								const repo = m[2] as string;
								const refAndPath = m[3] as string;
								const hash = (m[4] as string) || "";
								const firstSlash = refAndPath.indexOf("/");
								if (firstSlash > -1) {
									const refspec = refAndPath.substring(0, firstSlash);
									const filePath = refAndPath.substring(firstSlash + 1);
									let startLine = 0;
									let endLine: number | null = null;
									const lineMatch = hash.match(/^L(\d+)(?:-L(\d+))?$/);
									if (lineMatch) {
										startLine = parseInt(lineMatch[1] as string, 10);
										endLine = lineMatch[2]
											? parseInt(lineMatch[2] as string, 10)
											: null;
									}

									// Transform into a code block MDAST node with special meta
									// This ensures remark-prosemirror treats it as block-level
									const codeNode: MdastCode = {
										type: "code",
										lang: null,
										meta: `__code_snippet_block__ url:${href} owner:${owner} repo:${repo} refspec:${refspec} filePath:${filePath} startLine:${startLine}${endLine ? ` endLine:${endLine}` : ""}`,
										value: "",
									};
									parent.children.splice(index, 1, codeNode);
									return SKIP;
								}
							}
						}
					}
				});
			},
		],
	});
}

export function remarkJoinHtml(this: Processor) {
	const data = this.data();

	const fromMarkdownExtensions =
		data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);

	fromMarkdownExtensions.push({
		transforms: [
			function (tree) {
				let startIndex: number | null = null;
				const openTags: string[] = [];
				const splices: { parent: Parent; startIndex: number; endIndex: number }[] = [];

				visit(tree, "html", function (html, index, parent) {
					if (!parent || index === undefined) {
						return CONTINUE;
					}

					// Skip self-contained elements (those that have both start and end tag in same node)
					// This is common for inline elements like <span>...</span> used for page links
					const selfContainedMatch = html.value.match(
						/^<([A-Za-z][A-Za-z0-9-]*)[^>]*>.*<\/\1>$/s
					);
					if (selfContainedMatch) {
						return CONTINUE;
					}

					const endTag = html.value.match(/<\/([A-Za-z][A-Za-z0-9-]*)>$/)?.[1];
					if (endTag && startIndex !== null) {
						for (let i = openTags.length - 1; i >= 0; i--) {
							const startTag = openTags[i]!;
							if (startTag === endTag) {
								if (i === 0) {
									splices.push({ parent, startIndex, endIndex: index });
									openTags.splice(0, 1);
									startIndex = null;

									return CONTINUE;
								}

								openTags.splice(i, openTags.length - i);
							}
						}
					}

					const match = html.value.match(/<([A-Za-z][A-Za-z0-9-]*)/);
					if (!match) {
						return CONTINUE;
					}

					const [, tagName] = match;
					if (!tagName) {
						return CONTINUE;
					}

					openTags.push(tagName);

					if (startIndex === null) {
						startIndex = index;
					}
				});
				for (let i = 0; i < splices.length; i++) {
					const { parent, startIndex, endIndex } = splices[i]!;

					const nodes = parent.children.slice(startIndex, endIndex + 1);

					const reserialized = stringifyMdast({
						type: "root",
						children: nodes,
					});

					(parent.children[startIndex] as MdastHtml).value = reserialized;
					parent.children.splice(startIndex + 1, endIndex - startIndex);

					for (let j = i; j < splices.length; j++) {
						const laterSplice = splices[j]!;
						if (laterSplice.parent !== parent) {
							continue;
						}
						laterSplice.startIndex -= endIndex - startIndex;
						laterSplice.endIndex -= endIndex - startIndex;
					}
				}
			},
		],
	});
}

// Return nothing for nodes that are ignored.
function ignore() {
	return null;
}

/**
 * Completely ignore the element and just process the children.
 *
 * Mostly to prevent crashes for Markdown files in the wild that contain random stuff, for example
 * `jj` docs contain(ed?) a <div> at the top of the doc. You would get back an error like "Invalid
 * content for node doc" because the parser otherwise doesn't know about, and can't handle, <div>
 * elements.
 *
 * This handler fixes both the top-levle and recursive parsing children through the same pipeline.
 * The wrapper element is discarded, but content inside (headings, paragraphs, etc.) is preserved.
 * This is safe because we do not want to use these elements anywhere else in the app.
 */
function ignoreElAndProcessChildren(node: Parameters<HastNodeHandler>[0]) {
	if (node.type !== "element" || !("children" in node)) {
		// Not needed? The caller code seems to never exercise this case, but the types don't work
		// without it.
		return null;
	}
	const htmlValue = toHtml({ type: "root", children: node.children });
	if (!htmlValue.trim()) {
		// Empty HTML elements need a valid block node, not null (which crashes at doc root)
		return schema.nodes.paragraph.create();
	}
	const content = processPage(htmlValue).result.content.content;
	// Convert readonly array to mutable array for type compatibility
	return Array.from(content) as PmNode[];
}

// Type alias for the HTML handler function
type HastNodeHandler = NonNullable<RemarkProseMirrorOptions["htmlHandlers"]>[string];

/**
 * Parse the body of a ```Tldraw code block into a tldraw_embed node.
 * The body contains key:value pairs separated by newlines or spaces, e.g.:
 *   jsonPath:pages/abc/index.json
 *   documentId:abc pageId:def workspace:my-workspace
 */
function parseTldrawEmbed(body: string) {
	const attrs: Record<string, string | null> = {
		jsonPath: null,
		documentId: null,
		pageId: null,
		workspace: null,
	};

	const parts = body.trim().split(/\s+/);
	for (const part of parts) {
		const colonIdx = part.indexOf(":");
		if (colonIdx === -1) continue;
		const key = part.substring(0, colonIdx);
		const value = part.substring(colonIdx + 1);
		if (key in attrs) {
			attrs[key] = value;
		}
	}

	return schema.nodes.tldraw_embed.create(attrs);
}

export const options: RemarkProseMirrorOptions = {
	schema,
	htmlHandlers: {
		////////////////////////////////////////////////////////////////////////////////////////////
		// IMPORTANT: PREVENT PARSER CRASHES. We do not use these HTML elements ANYWHERE ELSE IN THE
		// APP (this is why we crash when we encounter them), so we need to handle them as a special
		// case.
		//
		// Let's say this again because it's important:
		//
		//     !!! This is safe because WE DO NOT USE THESE HANDLERS FOR ANYHING ELSE IN THE APP !!!
		//
		// If you EVER use these handlers for anything else, you WILL cause weird bugs that cause
		// REAL PEOPLE WHO YOU LOVE TO SUFFER!!!
		div: ignoreElAndProcessChildren,
		article: ignoreElAndProcessChildren,
		aside: ignoreElAndProcessChildren,
		figcaption: ignoreElAndProcessChildren,
		figure: ignoreElAndProcessChildren,
		footer: ignoreElAndProcessChildren,
		header: ignoreElAndProcessChildren,
		main: ignoreElAndProcessChildren,
		nav: ignoreElAndProcessChildren,
		// When a paragraph-wrapped block appears as raw HTML (e.g. `<p><img ...></p>`),
		// remark-prosemirror's default html dispatcher only passes `body.children[0]`,
		// so the <p> itself is what reaches us here. Unwrap and reprocess the children
		// so the inner <img> is handled by the `img` hast handler below. Without this,
		// the raw HTML falls through to `schema.text(node.value)` and crashes the doc.
		p: ignoreElAndProcessChildren,
		////////////////////////////////////////////////////////////////////////////////////////////

		a(node, _) {
			if (node.type !== "element") {
				return null;
			}

			const href = node.properties["href"];
			if (typeof href !== "string") {
				return null;
			}

			const normalized =
				href.startsWith("http://") || href.startsWith("https://")
					? href
					: `https://${href}`;

			// Handle GitHub code snippet links: github.com/<org>/<repo>/blob/<refspec>/<path>#Lx[-Ly]
			if (normalized.includes("github.com/") && normalized.includes("/blob/")) {
				try {
					const m = normalized.match(
						/^https?:\/\/(?:www\.)?github\.com\/([^/\s]+)\/([^/\s]+)\/blob\/([^#]+)(?:#(.*))?$/
					);
					if (m) {
						const owner = m[1] as string;
						const repo = m[2] as string;
						const refAndPath = m[3] as string;
						const hash = (m[4] as string) || "";
						const firstSlash = refAndPath.indexOf("/");
						if (firstSlash > -1) {
							const refspec = refAndPath.substring(0, firstSlash);
							const filePath = refAndPath.substring(firstSlash + 1);
							let startLine = 0;
							let endLine: number | null = null;
							const lineMatch = hash.match(/^L(\d+)(?:-L(\d+))?$/);
							if (lineMatch) {
								startLine = parseInt(lineMatch[1] as string, 10);
								endLine = lineMatch[2]
									? parseInt(lineMatch[2] as string, 10)
									: null;
							}

							// Check for data-expanded attribute to determine if this should be a block
							// Note: hast-util-from-html converts data-expanded to dataExpanded (camelCase)
							const dataExpanded =
								node.properties["data-expanded"] || node.properties["dataExpanded"];
							const isExpanded = dataExpanded === "true" || dataExpanded === true;

							const nodeAttrs = {
								url: normalized,
								owner,
								repo,
								refspec,
								filePath,
								startLine,
								endLine,
								expanded: isExpanded,
							};

							// Create block node if expanded, otherwise create inline node
							if (isExpanded) {
								try {
									const blockNode =
										schema.nodes.code_snippet_block.create(nodeAttrs);
									return blockNode;
								} catch (error) {
									console.error("Failed to create code_snippet_block:", error);
									return schema.nodes.code_snippet.create(nodeAttrs);
								}
							} else {
								return schema.nodes.code_snippet.create(nodeAttrs);
							}
						}
					}
				} catch {
					// ignore parse errors and fall through
				}
			}

			// Handle GitHub PR links
			const prMatch = normalized.match(
				/^(https?:\/\/)?(www\.)?github\.com\/([^/\s]+)\/([^/\s]+)\/pull\/(\d+)(?:[/?#]|$)/
			);
			if (prMatch) {
				const owner = prMatch[3] as string;
				const repo = prMatch[4] as string;
				const prNumber = parseInt(prMatch[5] as string, 10);

				const url = normalized;

				return schema.nodes.pull_request.create({ url, owner, repo, prNumber });
			}

			return null;
		},
		img(node, _) {
			if (node.type !== "element") {
				return null;
			}
			const file = node.properties["dataFile"];
			const src = file ? null : node.properties["src"];
			const width = node.properties["width"];
			const alignment = node.properties["dataAlignment"];
			const aspectRatio =
				typeof node.properties["dataAspectRatio"] === "string"
					? parseFloat(node.properties["dataAspectRatio"])
					: undefined;
			const result = schema.nodes.image.create({
				src,
				file,
				alignment,
				width,
				aspectRatio,
			});
			return result;
		},
		br(node, parent) {
			if (node.type !== "element") {
				return null;
			}
			// For an actual hard break within a paragraph that
			// has other content, produce a hard break node
			if (parent && parent.children.length !== 1) {
				return schema.nodes.hard_break.create();
			}
			// But if this was just a top-level hard break on its
			// own, we left it as a placeholder for an empty paragraph
			return schema.nodes.paragraph.create();
		},
		section(node) {
			if (node.type !== "element") {
				return null;
			}

			const htmlValue = toHtml({ type: "root", children: node.children });

			if (node.properties["readOnly"] || Object.hasOwn(node.properties, "readonly")) {
				const isManagedSection =
					node.properties["dataVariant"] === "moment-agents-template";
				// Store raw content as an attribute instead of parsing into children.
				// This preserves HTML comments (managed section markers, version line)
				// that would otherwise be lost during a processPage round-trip.
				return schema.nodes.readonly_section.create({
					managedSection: isManagedSection,
					rawContent: htmlValue,
				});
			}

			if (node.properties["dataNodeType"] !== "columns") {
				return null;
			}

			const content = processPage(htmlValue).result.content.content;

			return schema.nodes.columns.create(
				{
					columns: node.properties["dataColumns"]
						? parseInt(node.properties["dataColumns"] as string, 10)
						: 2,
				},
				Array.from(content) as PmNode[]
			);
		},
		span(node) {
			if (node.type !== "element") {
				return null;
			}

			// Handle page links: <span data-page-link="true" data-page-title="...">
			const isPageLink =
				node.properties["dataPageLink"] === "true" ||
				node.properties["data-page-link"] === "true";

			if (isPageLink) {
				// hast-util-from-html already decodes HTML entities in attribute values
				// per the HTML spec (e.g., &amp; → &, &quot; → "), so we use the raw
				// property value directly. Calling decodeHtmlEntities here would cause
				// double-decoding: a title literally containing "&amp;" would be
				// corrupted to "&".
				const pageTitle =
					(typeof node.properties["dataPageTitle"] === "string"
						? node.properties["dataPageTitle"]
						: null) ||
					(typeof node.properties["data-page-title"] === "string"
						? node.properties["data-page-title"]
						: null) ||
					"";
				const pageDisplay =
					(typeof node.properties["dataPageDisplay"] === "string"
						? node.properties["dataPageDisplay"]
						: null) ||
					(typeof node.properties["data-page-display"] === "string"
						? node.properties["data-page-display"]
						: null) ||
					null;
				return schema.nodes.page_link.create({
					pageTitle,
					displayText: pageDisplay,
				});
			}

			return null;
		},
	},
	handlers: {
		yaml: ignore,
		definition: ignore,
		footnoteDefinition: ignore,
		paragraph: toPmNode(schema.nodes.paragraph),
		heading: toPmNode(schema.nodes.heading, (node) => ({ level: node.depth })),
		blockquote: toPmNode(schema.nodes.blockquote),
		break: toPmNode(schema.nodes.hard_break),
		code(node: MdastCode) {
			// Check if this is a transformed GitHub code snippet block
			if (node.meta?.startsWith("__code_snippet_block__")) {
				const metaParts = node.meta.split(" ");
				const attrs: Record<string, any> = {};
				for (const part of metaParts.slice(1)) {
					const [key, ...valueParts] = part.split(":");
					const value = valueParts.join(":");
					attrs[key] = value;
				}

				return schema.nodes.code_snippet_block.create({
					id: DEFAULT_CELL_ID,
					url: attrs["url"] || "",
					owner: attrs["owner"] || "",
					repo: attrs["repo"] || "",
					refspec: attrs["refspec"] || "",
					filePath: attrs["filePath"] || "",
					startLine: parseInt(attrs["startLine"] || "0", 10),
					endLine: attrs["endLine"] ? parseInt(attrs["endLine"], 10) : null,
					expanded: true,
				});
			}

			// TLDraw embed: ```Tldraw with key:value body
			const lowercaseLang = node.lang?.toLowerCase() ?? "";
			if (lowercaseLang === "tldraw") {
				return parseTldrawEmbed(node.value ?? "");
			}

			const [, cellId, cellName] = node.meta?.match(/cellId:(.*) cellName:(.*)/) ?? [];
			const child = node.value ? schema.text(node.value) : null;
			// NOT `toLocaleLowerCase` because in the Turkish local, the lowercase of "I" is the
			// dotless i, "ı". This obviously would not match the "i" in "javascript".
			const isJavaScript = ["javascript", "js", "jsx", "typescript", "ts", "tsx"].includes(
				lowercaseLang
			);
			if (isJavaScript && cellId && cellName) {
				return schema.nodes.compute_cell.create({ cellName, id: cellId }, child);
			}
			const result = schema.nodes.code_block.create(
				{ language: node.lang, meta: node.meta },
				child
			);
			return result;
		},
		emphasis: toPmMark(schema.marks.em),
		link: toPmMark(schema.marks.link, (node) => ({
			// Skip normalizeUri for page: protocol — it's our internal protocol
			// and percent-encoding spaces would corrupt the page title
			// (e.g., "page:My Page" → "page:My%20Page" breaks resolution).
			href: node.url.startsWith("page:") ? node.url : normalizeUri(node.url),
			title: node.title,
		})),
		strong: toPmMark(schema.marks.strong),
		// `delete` is strikethrough in Markdown. So-called because the HTML markup is often the
		// `<del>` tag.
		delete: toPmMark(schema.marks.strike),
		inlineCode(node) {
			return schema.text(node.value, [schema.marks.code.create()]);
		},
		image: toPmNode(schema.nodes.inline_image, (node) => ({
			src: node.url,
			alt: node.alt ?? "",
			title: node.title ?? null,
		})),
		// Resolve reference-style images (`![alt][id]` with a matching `[id]: url`
		// definition). remark-prosemirror has a built-in fallback for `linkReference`
		// but not for `imageReference`, so it would otherwise throw
		// "unknown markdown node: imageReference". The definition's url may be a
		// regular URL, a relative path, or a `data:` URI — we pass it through as-is.
		imageReference(node, _, state) {
			const id = String(node.identifier).toUpperCase();
			const definition = state.definitionById.get(id);
			if (!definition) {
				return null;
			}
			return schema.nodes.inline_image.create({
				src: definition.url,
				alt: node.alt ?? "",
				title: definition.title ?? null,
			});
		},
		list(node: MdastList, _, state) {
			return state.all(node);
		},
		listItem(node: MdastListItem, list, state) {
			if (list?.type !== "list") {
				return null;
			}
			const children = state.all(node);
			if (node.checked == null) {
				return schema.nodes.list_item.create(
					{ indent: node.data?.["depth"], bullet: node.data?.["bullet"] },
					children
				);
			}
			return schema.nodes.to_do_item.create(
				{
					status: node.checked ? "completed" : "to do",
					indent: node.data?.["depth"],
				},
				children
			);
		},
		thematicBreak: toPmNode(schema.nodes.horizontal_rule),
		table(node: MdastTable, _, state) {
			const rows = node.children.map((row: MdastTableRow, rowIndex: number) => {
				const cells = row.children.map((cell) => {
					const children = state.all(cell);
					const nodeType =
						rowIndex === 0 ? schema.nodes.table_header : schema.nodes.table_cell;
					return nodeType.create(
						null,
						children.length > 0 ? children : schema.text("\u00A0")
					);
				});
				return schema.nodes.table_row.create(null, cells);
			});
			return schema.nodes.table.create(null, rows);
		},
		tableRow() {
			return null;
		},
		tableCell() {
			return null;
		},
	},
};

export function mdastFromProseMirror(node: PmNode) {
	return fromProseMirror(node, {
		schema,
		nodeHandlers: {
			page_link(node) {
				const pageTitle = node.attrs["pageTitle"] as string;
				const displayText = node.attrs["displayText"] as string | null;
				// Filter out page links without valid titles to avoid producing [[]]
				if (!pageTitle) {
					return null;
				}
				// Serialize as [[Page Title]] - using html type to avoid escaping brackets
				// The pageTitle is stored unescaped, so we need to escape ] and \ to prevent
				// breaking the [[...]] syntax. We don't escape / to preserve parent/child
				// disambiguation (e.g., [[Parent/Child]]).
				const escapedTitle = escapePageTitleForMarkdown(pageTitle);
				const escapedDisplayText = displayText
					? escapePageTitleForMarkdown(displayText)
					: null;
				return {
					type: "html",
					value: `[[${escapedTitle}${escapedDisplayText ? `|${escapedDisplayText}` : ""}]]`,
				} satisfies MdastHtml;
			},
			pull_request(node) {
				const url = node.attrs["url"] as string;
				const owner = node.attrs["owner"] as string;
				const repo = node.attrs["repo"] as string;
				const prNumber = node.attrs["prNumber"] as number;

				const linkText = `${owner}/${repo}#${prNumber}`;

				return {
					type: "html",
					value: toHtml({
						type: "root",
						children: [
							{
								type: "element",
								tagName: "a",
								properties: {
									"href": url,
									"data-pull-request": "true",
									"data-owner": node.attrs["owner"] as string,
									"data-repo": node.attrs["repo"] as string,
									"data-pr-number": node.attrs["prNumber"] as number,
								},
								children: [{ type: "text", value: linkText }],
							},
						],
					}),
				} satisfies MdastHtml;
			},
			code_snippet(node) {
				const url = node.attrs["url"] as string;
				const owner = node.attrs["owner"] as string;
				const repo = node.attrs["repo"] as string;
				const filePath = node.attrs["filePath"] as string;
				const startLine = (node.attrs["startLine"] as number) ?? 0;
				const endLine = (node.attrs["endLine"] as number | null) ?? null;
				const expanded = (node.attrs["expanded"] as boolean) ?? false;

				const lineText =
					startLine > 0 ? `#L${startLine}${endLine ? `-L${endLine}` : ""}` : "";
				const linkText = `${owner}/${repo}/${filePath}${lineText}`;
				return {
					type: "html",
					value: toHtml({
						type: "root",
						children: [
							{
								type: "element",
								tagName: "a",
								properties: {
									"href": url,
									"data-expanded": expanded ? "true" : "false",
								},
								children: [{ type: "text", value: linkText }],
							},
						],
					}),
				} satisfies MdastHtml;
			},
			code_snippet_block(node) {
				const url = node.attrs["url"] as string;
				const owner = node.attrs["owner"] as string;
				const repo = node.attrs["repo"] as string;
				const filePath = node.attrs["filePath"] as string;
				const startLine = (node.attrs["startLine"] as number) ?? 0;
				const endLine = (node.attrs["endLine"] as number | null) ?? null;
				const expanded = (node.attrs["expanded"] as boolean) ?? true; // block nodes are expanded by default

				const lineText =
					startLine > 0 ? `#L${startLine}${endLine ? `-L${endLine}` : ""}` : "";
				const linkText = `${owner}/${repo}/${filePath}${lineText}`;
				const html = toHtml({
					type: "root",
					children: [
						{
							type: "element",
							tagName: "a",
							properties: {
								"href": url,
								"data-expanded": expanded ? "true" : "false",
							},
							children: [{ type: "text", value: linkText }],
						},
					],
				});

				return {
					type: "html",
					value: html,
				} satisfies MdastHtml;
			},
			readonly_section(node) {
				// Output the stored raw content verbatim, preserving HTML comments
				// (managed section markers, version line) that would be lost if
				// the content were re-parsed through ProseMirror.
				const rawContent = ((node.attrs["rawContent"] as string) || "").trim();
				const variantAttr = node.attrs["managedSection"]
					? ' data-variant="moment-agents-template"'
					: "";
				return {
					type: "paragraph",
					children: [
						{
							type: "html",
							value: `<section readonly${variantAttr}>\n${rawContent}\n</section>`,
						},
					],
				};
			},
			tldraw_embed(node) {
				const pairs: string[] = [];
				if (node.attrs["jsonPath"]) {
					pairs.push(`jsonPath:${node.attrs["jsonPath"]}`);
				}
				if (node.attrs["documentId"]) {
					pairs.push(`documentId:${node.attrs["documentId"]}`);
				}
				if (node.attrs["pageId"]) {
					pairs.push(`pageId:${node.attrs["pageId"]}`);
				}
				if (node.attrs["workspace"]) {
					pairs.push(`workspace:${node.attrs["workspace"]}`);
				}
				return {
					type: "code" as const,
					lang: "Tldraw",
					meta: null,
					value: pairs.join("\n"),
				};
			},
			compute_cell: fromPmNode("code", (node) => {
				const id = node.attrs["id"];
				const cellName = node.attrs["cellName"];

				return {
					lang: "JavaScript",
					meta: `cellId:${id} cellName:${cellName}`,
					value: node.textContent,
				};
			}),
			blockquote: fromPmNode("blockquote"),
			code_block: fromPmNode("code", (node) => {
				const lang = node.attrs["language"];
				return {
					lang: lang === "plaintext" ? null : (lang ?? null),
					meta: node.attrs["meta"],
					value: node.textContent,
				};
			}),
			heading: fromPmNode("heading", (node) => ({ depth: node.attrs["level"] })),
			horizontal_rule: fromPmNode("thematicBreak"),
			to_do_item: fromPmNode("listItem", (node) => ({
				data: {
					depth: node.attrs["indent"],
				},
				checked: node.attrs["status"] === "completed",
			})),
			list_item: fromPmNode("listItem", (node) => ({
				data: {
					depth: node.attrs["indent"],
					bullet: node.attrs["bullet"],
				},
			})),
			paragraph(node, _, state) {
				const children = state.all(node) as PhrasingContent[];
				if (children.length === 0) {
					children.push({
						type: "html",
						value: "<br>",
					});
				}
				return {
					type: "paragraph",
					children,
				};
			},
			image(node) {
				return {
					type: "paragraph",
					children: [
						{
							type: "html",
							value: toHtml({
								type: "root",
								children: [
									{
										type: "element",
										tagName: "img",
										properties: {
											src:
												node.attrs["src"] ??
												(node.attrs["file"]
													? `related/${node.attrs["file"]}`
													: ""),
											...(node.attrs["alt"] && { alt: node.attrs["alt"] }),
											...(node.attrs["width"] && {
												width: node.attrs["width"],
											}),
											...(node.attrs["aspectRatio"] && {
												"style": `aspect-ratio: ${node.attrs["aspectRatio"]}`,
												"data-aspect-ratio": node.attrs["aspectRatio"],
											}),
											...(node.attrs["alignment"] && {
												"data-alignment": node.attrs["alignment"],
											}),
											...(node.attrs["file"] && {
												"data-file": node.attrs["file"],
											}),
										},
										children: [],
									},
								],
							}),
						} satisfies MdastHtml,
					],
				};
			},
			inline_image(node) {
				return {
					type: "image",
					url: node.attrs["src"],
					alt: node.attrs["alt"] || null,
					title: node.attrs["title"] || null,
				} satisfies MdastImage;
			},
			table(node, _, state) {
				const rows = state.all(node) as MdastTableRow[];
				return {
					type: "table" as const,
					align: Array.from({ length: rows[0]?.children.length ?? 0 }, () => null),
					children: rows,
				};
			},
			table_row(node, _, state) {
				return {
					type: "tableRow" as const,
					children: state.all(node),
				} as MdastTableRow;
			},
			table_header(node, _, state) {
				return {
					type: "tableCell" as const,
					children: state.all(node) as PhrasingContent[],
				};
			},
			table_cell(node, _, state) {
				return {
					type: "tableCell" as const,
					children: state.all(node) as PhrasingContent[],
				};
			},
			hard_break: fromPmNode("break"),
			columns(node, _, state) {
				const reserialized = stringifyMdast({
					type: "root",
					children: state.all(node) as RootContent[],
				});
				const hastRoot = fromHtml(reserialized);
				const html = hastRoot.children[0];
				if (html?.type !== "element") {
					return null;
				}
				const [, body] = html.children;
				if (body?.type !== "element") {
					return null;
				}
				const children = body.children;
				return {
					type: "paragraph",
					children: [
						{
							type: "html",
							value: toHtml({
								type: "root",
								children: [
									{
										type: "element",
										tagName: "section",
										properties: {
											dataNodeType: "columns",
											dataColumns: node.attrs["columns"].toString(),
										},
										children: [{ type: "text", value: "\n" }, ...children],
									},
								],
							}),
						},
					],
				};
			},
		},
		markHandlers: {
			em: fromPmMark("emphasis"),
			strong: fromPmMark("strong"),
			strike: fromPmMark("delete"),
			link: fromPmMark("link", (mark) => ({
				url: mark.attrs["href"],
				title: mark.attrs["title"],
			})),
			code(_mark, _, children) {
				const value = children[0]?.type === "text" ? children[0].value : "";
				return {
					type: "inlineCode",
					value,
				};
			},
		},
	});
}

function processPage(md: string) {
	return unified()
		.use(remarkParse)
		.use(remarkStrikethrough)
		.use(remarkGfmTable)
		.use(remarkTaskList)
		.use(remarkArbitraryListDepth)
		.use(remarkUnwrapHtml)
		.use(remarkJoinHtml)
		.use(remarkTransformCodeSnippets) // Must run after remarkJoinHtml
		.use(remarkPageLink) // Transform [[pageId]] to HTML before ProseMirror conversion
		.use(remarkFrontmatter, ["yaml"])
		.use(remarkYamlMatter)
		.use(remarkProseMirror, options)
		.processSync(md);
}

export const parsePage = (_md: string): { doc: JsonObject; metadata?: Record<string, unknown> } => {
	const md = _md.trim().length === 0 ? "<br />" : _md;
	const file = processPage(md);

	const pmDoc = file.result;
	const metadata = file.data.matter;
	return { metadata, doc: pmDoc.toJSON() };
};

function stringifyMdast(mdast: Root) {
	const embeddedWikiLinkPlaceholder = "\uE000MOMENTEMBEDWIKILINKOPEN\uE001";
	const adjacentPageLinkBangPlaceholder = "\uE000MOMENTPAGELINKBANG\uE001";

	const markEmbeddedWikiLinksInText = (value: string): string => {
		let result = "";
		let index = 0;

		while (index < value.length) {
			if (value.startsWith("![[", index)) {
				let backslashCount = 0;
				for (let backslashIndex = index - 1; backslashIndex >= 0; backslashIndex -= 1) {
					if (value[backslashIndex] !== "\\") {
						break;
					}
					backslashCount += 1;
				}

				if (backslashCount % 2 === 0) {
					result += embeddedWikiLinkPlaceholder;
				} else {
					result += "![[";
				}
				index += 3;
				continue;
			}

			result += value[index];
			index += 1;
		}

		return result;
	};

	visit(mdast, "text", (node) => {
		node.value = markEmbeddedWikiLinksInText(node.value);
	});

	visit(mdast, (node) => {
		if (!("children" in node) || !Array.isArray(node.children)) {
			return;
		}

		for (let index = 0; index < node.children.length - 1; index += 1) {
			const currentChild = node.children[index];
			const nextChild = node.children[index + 1];

			if (
				currentChild?.type !== "text" ||
				typeof currentChild.value !== "string" ||
				!currentChild.value.endsWith("!") ||
				nextChild?.type !== "html" ||
				typeof nextChild.value !== "string" ||
				!nextChild.value.startsWith("[[")
			) {
				continue;
			}

			currentChild.value = `${currentChild.value.slice(0, -1)}${adjacentPageLinkBangPlaceholder}`;
		}
	});

	// NOTE: Do NOT use remarkDirective here as it adds an unsafe pattern that
	// escapes colons in URLs like `page:Title` → `page\:Title`.
	// We don't produce any directives in our output, so we don't need it.
	const markdown = unified()
		.use(remarkStrikethrough)
		.use(remarkGfmTable)
		.use(remarkArbitraryListDepth)
		.use(remarkStringify, {
			rule: "-",
		})
		.use(remarkFrontmatter, ["yaml"])
		.stringify(mdast);

	return markdown
		.replaceAll(embeddedWikiLinkPlaceholder, "\\![[")
		.replaceAll(adjacentPageLinkBangPlaceholder, "&#33;");
}

export const serializePage = (node: PmNode, metadata?: Record<string, unknown>) => {
	// Apply suggestions to get the "accepted" state before serializing.
	// This removes deletions and strips insertion/modification marks, ensuring
	// external tools see clean content without tracked changes markup.
	const { projection } = buildAcceptedProjectionWithMapping(node);
	const safeNode = transformStripTrailingSpaceMarks(projection);
	const mdast = mdastFromProseMirror(safeNode);
	if (metadata && Object.keys(metadata).length > 0) {
		// Insert the YAML as the first child of the root node
		mdast.children.unshift({ type: "yaml", value: stringify(metadata) } satisfies Yaml);
	}

	return stringifyMdast(mdast);
};
